import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'style/layout/Table.css';
import MaterialTable, {
  Column,
  EditCellColumnDef,
  Filter,
} from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core';
import translations from '../../../services/translations/translations';
import useDateLocale from '../../../services/translations/useDateLocale';
import PrimaryButton from '../buttons/PrimaryButton';

const BasicMaterialTable = ({
  fetchData,
  columns,
  sizePerPage,
  setSelectedRow,
  localStorageTag,
  fitToWindow,
  refreshKey,
  rowStyle,
  parentChildData,
  className,
  headerBackground = '#f0f0f0',
}: {
  // A class of the page's own for the look of its table
  className?: string;
  // The background of the headings that are not filtered by
  headerBackground?: string;
  // The row a row is shown under (opened by the page itself, e.g. with a + of its own);
  // such rows are not counted in the rows of a page
  parentChildData?: (row: any, rows: any[]) => any;
  fetchData: any;
  columns: any;
  sizePerPage: any;
  setSelectedRow: any;
  localStorageTag: string;
  // A change of this reads the page again, e.g. rows opened under another row
  refreshKey?: number;
  // The style of a row by its data
  rowStyle?: (rowData: any) => React.CSSProperties;
  // The rows scroll inside the table, which fits the window: a wide table (e.g. with the
  // browser zoomed in) has its horizontal scroll bar always in sight, not only below the
  // last row, and the headings stay in place
  fitToWindow?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.common]);
  const dateLocale = useDateLocale();
  const tableRef: any = React.createRef();
  const [highlightedColumns, setHighlightedColumns] = useState<
    (string | undefined)[]
  >([]);

  // The filter row is the first row of the body, so it is kept in place under the
  // headings at their height, which changes with their text and the window
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const head = wrapperRef.current?.querySelector('thead');
    if (!fitToWindow || !head || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() =>
      wrapperRef.current?.style.setProperty(
        '--material-table-head-height',
        `${head.getBoundingClientRect().height}px`
      )
    );
    observer.observe(head);
    return () => observer.disconnect();
  });

  const rowEvents = (rowData: any) => {
    setSelectedRow(rowData);
  };

  const refreshQuery = () => {
    if (tableRef != null && tableRef.current != null) {
      tableRef.current.onQueryChange();
    }
  };

  // The page is read again with the same filters when the key changes (not at the start)
  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) {
      firstRefresh.current = false;
      return;
    }
    refreshQuery();
  }, [refreshKey]);

  const clearFilter = () => {
    if (tableRef?.current != null) {
      // Needed to reset the filter
      tableRef.current.state.query.filters = [];
      // Needed to reset the UI
      tableRef.current.state.columns.forEach((col: EditCellColumnDef) => {
        col.tableData.filterValue = '';
      });
      refreshQuery();
    }
  };

  const highlightFilteredColumns = (filters: Filter<Record<string, any>>[]) => {
    const filteredColumns = filters
      .filter((f: Filter<Record<string, any>>) => {
        return (
          f.column !== null &&
          ((typeof f.value === 'number' && f.value !== null) ||
            (typeof f.value === 'string' && f.value !== '') ||
            (Array.isArray(f.value) && f.value.length > 0) ||
            (!Array.isArray(f.value) && typeof f.value === 'object'))
        );
      })
      .map((f: Filter<Record<string, any>>) => {
        return f.column.field;
      });
    highlightColumns(filteredColumns);
  };

  const highlightColumns = (columnsToHighlight: (string | undefined)[]) => {
    const toHighlight: string[] = [];
    tableRef?.current?.state.columns.forEach(
      (col: Column<Record<string, any>>) => {
        if (col.field && columnsToHighlight.indexOf(col.field) !== -1) {
          toHighlight.push(col.field);
        }
      }
    );
    setHighlightedColumns(toHighlight);
  };

  const localization = {
    pagination: {
      labelRowsSelect: t(`${translations.common}:Row`),
      firstTooltip: t(`${translations.common}:First Page`),
      lastTooltip: t(`${translations.common}:Last Page`),
      nextTooltip: t(`${translations.common}:Next Page`),
      previousTooltip: t(`${translations.common}:Previous Page`),
    },
    body: {
      dateTimePickerLocalization: dateLocale.dateFns,
      emptyDataSourceMessage: t(`${translations.common}:No data to show`),
    },
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: '#4d8549',
      },
      secondary: {
        main: '#9fd03e',
      },
    },
    typography: {
      fontFamily: ['futura-pt', 'sans-serif'].join(','),
    },
  });

  const onFilterChange = (filters: any) => {
    window.localStorage.setItem(localStorageTag, JSON.stringify(filters));
    highlightFilteredColumns(filters);
  };

  const addHeaderStyle = (column: any) => {
    const style = column.headerStyle;
    column.headerStyle = {
      ...style,
      // Opaque, as the headings stay in place over the rows scrolled under them
      backgroundColor:
        highlightedColumns.indexOf(column.field) !== -1
          ? 'var(--theme-secondary-color)'
          : headerBackground,
      color:
        highlightedColumns.indexOf(column.field) !== -1
          ? 'var(--theme-background-color)'
          : '',
    };
    return column;
  };

  const columnsForTable: any[] = columns.map((c: any) => {
    return addHeaderStyle(c);
  });

  const sizePerPageList = [10, 50, 100, 200, 250];
  return (
    <div
      className={`basic-material-table ${
        fitToWindow ? 'basic-material-table-fit' : ''
      } ${parentChildData ? 'basic-material-table-tree' : ''} ${
        className ?? ''
      }`}
      ref={wrapperRef}
    >
      <MuiThemeProvider theme={theme}>
        <PrimaryButton
          onClick={() => {
            clearFilter();
          }}
        >
          {t(`${translations.common}:Clear`).toLocaleUpperCase()}
        </PrimaryButton>
        <MaterialTable
          key={`basic-material-table-${columns.length}`}
          title=""
          localization={localization}
          tableRef={tableRef}
          columns={columnsForTable}
          data={async (query) => {
            onFilterChange(query.filters);

            const result = await fetchData(query);
            return {
              data: result.data,
              page: result.page,
              totalCount: result.totalCount,
            };
          }}
          onRowClick={(event, rowData) => rowEvents(rowData)}
          {...(parentChildData ? { parentChildData } : {})}
          options={{
            ...(fitToWindow
              ? { maxBodyHeight: 'calc(100vh - 300px)', minBodyHeight: 300 }
              : {}),
            ...(rowStyle ? { rowStyle } : {}),
            toolbar: false,
            filtering: true,
            pageSize: sizePerPage,
            emptyRowsWhenPaging: false,
            pageSizeOptions: sizePerPageList,
            search: false,
            headerStyle: {
              backgroundColor: headerBackground,
              fontWeight: 'bold',
            },
          }}
        />
      </MuiThemeProvider>
    </div>
  );
};

export default BasicMaterialTable;
